import { observer } from 'mobx-react';
import React from 'react';

import { GridLegacy as Grid, Checkbox } from '@mui/material';

import dayjs, { Dayjs } from 'dayjs';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';

import { Form, Formik, FormikHelpers } from 'formik';

import { isEqual } from 'lodash';

import { AxiosError } from 'axios';

import { trackHubSpotEvent } from 'utils/hubspot';

import PhoneInput from 'ui/PhoneInput';
import { isCountryCodeOnly } from 'utils/phone';

import { CenteredCircularProgress } from 'components/Progress';
import { Button } from 'components/Button';
import NavigationBlocker from 'components/NavigationBlocker';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import { StyledDatePicker, StyledTextField } from 'components/Input/styles';
import InfiniteAutocomplete from 'components/InfiniteAutocomplete';

import API from 'utils/api';

import { UpdatedUserPayload } from 'utils/events/types';

import { postMessage } from 'utils/events/broadcast';
import { EventType } from 'utils/events/constants';

import { UserRole } from 'core/constants';

import { ADMIN_AND_ACCOUNT_MANAGERS } from 'utils/roles/roleGroups';

import { SearchResponse } from 'utils/api/types';
import { EditUserRequest, UserAttributesUpdate } from 'utils/api/modules/user/user.types';
import { formErrors, getErrorMessage, isBadRequest } from 'utils/api/errors';

import Chip from 'components/Chip';

import Logger from 'utils/logger';

import { useStore } from '../../../../utils/hooks/store';
import { StyledSectionGrid, StyledRowTitle, ButtonWrapper, StyledOption } from './styles';
import { EditPersonalInformationSchema, PersonalInformationFormValues } from './types';

import useMappedFields from '../../hooks/useMappedFields';
import { IDP_FIELDS } from '../IdpManaged';

const loadingOption = { uuid: 'loading', name: 'Loading...' } as SearchResponse;

const EditRow: React.FC<{
  name: string;
  disabled?: boolean;
  children: React.ReactNode;
  id?: string;
}> = ({ name, disabled = false, children, id }): React.ReactElement => (
  <>
    <StyledSectionGrid item container xs={12} alignContent="flex-start" id={id} marginTop="16px">
      <StyledRowTitle $disabled={disabled}>{name}</StyledRowTitle>
    </StyledSectionGrid>
    <StyledSectionGrid item container xs={12}>
      {children}
    </StyledSectionGrid>
  </>
);

export const PersonalInformation: React.FunctionComponent<{
  setEditing: (val: boolean) => void;
}> = ({ setEditing }) => {
  const {
    userDetails: { details },
    notification,
    session,
  } = useStore();
  const { isMapped, scimEnabled } = useMappedFields();
  const { results, searchQuery, loading, loadingMore, hasMore, loadMore } = useInfiniteSearch(API.searchGroups);
  if (!details) {
    return null;
  }

  const initialValues: PersonalInformationFormValues = {
    name: details.name,
    email: details.email,
    phoneNumber: details.phoneNumber,
    employeeId: details.attributes?.employeeId ?? '',
    personalDetails: details.personalDetails.initialValues,
    groups: details.groups.map((g) => ({ uuid: g.uuid, name: g.name })),
  };

  const onUpdate = async (
    data: PersonalInformationFormValues,
    helpers: FormikHelpers<PersonalInformationFormValues>
  ): Promise<void> => {
    const oldPhoneNumber = details.phoneNumber;
    try {
      helpers.setSubmitting(true);

      const isUser = session.user?.role === UserRole.User;
      const canEditOrgAttributes = ADMIN_AND_ACCOUNT_MANAGERS.includes(session.user?.role as UserRole);

      const formattedData: EditUserRequest = {};
      if (data.name !== initialValues.name) {
        formattedData.name = data.name;
      }
      if (data.phoneNumber !== initialValues.phoneNumber) {
        formattedData.phoneNumber = data.phoneNumber;
      }
      if (data.email !== initialValues.email) {
        formattedData.email = data.email;
      }
      if (!isUser && !isEqual(data.groups, initialValues.groups)) {
        formattedData.groups = data.groups;
      }
      if (!isEqual(data.personalDetails, initialValues.personalDetails)) {
        formattedData.personalDetails = data.personalDetails;
      }
      if (canEditOrgAttributes && data.employeeId !== initialValues.employeeId) {
        const attributes: UserAttributesUpdate = { employeeId: data.employeeId };
        formattedData.attributes = attributes;
      }

      const response = await API.user.updateUserData(details.uuid, formattedData)();
      const payload: UpdatedUserPayload = response.data;
      postMessage(EventType.UpdatedUser, payload);
      notification.enqueueSuccessSnackbar('User details updated successfully!');
      // if user added a phone number send tracking events
      if (data.phoneNumber && !oldPhoneNumber) {
        if (session.hasHubSpot) {
          trackHubSpotEvent('Phone number added');
        }
      }

      helpers.setStatus();
      helpers.setErrors({});
      setEditing(false);
    } catch (e) {
      const error = e as AxiosError;
      const errorMessage = getErrorMessage(error, 'Could not update user details');
      notification.enqueueErrorSnackbar(errorMessage);
      const { nonFieldErrors, fieldErrors } = formErrors(error);
      fieldErrors && helpers.setErrors(fieldErrors);
      nonFieldErrors && helpers.setStatus(nonFieldErrors);

      if (!isBadRequest(error)) {
        Logger.error('Invalid update user details API response', e);
      }
    } finally {
      helpers.setSubmitting(false);
    }
  };
  const isUser = session.user?.role === UserRole.User;
  const canEditOrgAttributes = ADMIN_AND_ACCOUNT_MANAGERS.includes(session.user?.role as UserRole);
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(values, helpers) => onUpdate(values, helpers)}
      validationSchema={EditPersonalInformationSchema(details.showSafety)}
    >
      {({ values, handleChange, touched, errors, isSubmitting, setFieldValue, dirty }) => (
        <Form>
          <NavigationBlocker
            when={dirty}
            shouldBlockNavigation={() => dirty}
            navigateMessage="You have unsaved changes. Do you want to discard them or stay on this page?"
          />
          <Grid container sx={{ padding: '20px' }}>
            <EditRow name="Name">
              <StyledTextField
                id="name"
                name="name"
                value={values.name}
                helperText={touched.name ? errors.name : ''}
                error={(touched.name || false) && errors.name !== undefined}
                onChange={handleChange}
                fullWidth
              />
            </EditRow>

            <EditRow name="Email">
              <StyledTextField
                id="email"
                name="email"
                value={values.email}
                helperText={touched.email ? errors.email : ''}
                error={(touched.email || false) && errors.email !== undefined}
                onChange={handleChange}
                disabled={isSubmitting || scimEnabled}
                fullWidth
              />
            </EditRow>
            <EditRow name="Date of birth">
              <StyledDatePicker
                sx={{ width: '100%' }}
                defaultValue={
                  values.personalDetails.birthDate ? dayjs(values.personalDetails.birthDate as string) : null
                }
                onChange={(date: Dayjs | null) => {
                  if (date === null) {
                    setFieldValue('personalDetails.birthDate', null);
                  } else if (date.isValid()) {
                    setFieldValue('personalDetails.birthDate', date.format('YYYY-MM-DD'));
                  }
                }}
                slotProps={{
                  field: { clearable: true },
                  textField: {
                    error:
                      (touched.personalDetails?.birthDate || false) && errors.personalDetails?.birthDate !== undefined,
                    helperText: touched.personalDetails?.birthDate ? errors.personalDetails?.birthDate : '',
                  },
                }}
                format="DD/MM/YYYY"
              />
            </EditRow>
            <EditRow name="Phone number">
              <PhoneInput
                disabled={scimEnabled}
                style={{ width: '100%' }}
                defaultCountry="gb"
                preferredCountries={['gb', 'ie', 'us']}
                regions={['north-america', 'europe', 'middle-east'] as unknown as [string]}
                value={values.phoneNumber}
                onChange={(e) => {
                  if (isCountryCodeOnly(e as string)) {
                    setFieldValue('phoneNumber', null);
                  } else {
                    setFieldValue('phoneNumber', e);
                  }
                }}
                variant="outlined"
                disableAreaCodes
                placeholder="e.g. +44 0123456789"
                error={(touched.phoneNumber || false) && errors.phoneNumber !== undefined}
                helperText={touched.phoneNumber ? errors.phoneNumber : ''}
              />
            </EditRow>

            {isUser ? null : (
              <EditRow name="Teams">
                <InfiniteAutocomplete
                  loadingMore={loadingMore}
                  hasMore={hasMore}
                  onLoadMore={loadMore}
                  multiple
                  id="team"
                  options={results}
                  value={values.groups}
                  disableCloseOnSelect
                  renderOption={(props, option: SearchResponse, { selected }) =>
                    option.uuid === loadingOption.uuid ? (
                      <StyledOption>
                        <CenteredCircularProgress />
                      </StyledOption>
                    ) : (
                      <StyledOption {...props}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="medium" />}
                          checkedIcon={<CheckBoxIcon fontSize="medium" />}
                          checked={selected}
                        />
                        {option.name}
                      </StyledOption>
                    )
                  }
                  getOptionLabel={(option: SearchResponse | null) => option?.name as string}
                  renderInput={(params) => (
                    <StyledTextField
                      {...params}
                      placeholder={values.groups.length === 0 ? 'Choose option(s)' : ''}
                      onChange={(e) => {
                        searchQuery(e.target.value);
                      }}
                    />
                  )}
                  renderTags={(tagValue, getTagProps) =>
                    tagValue.map((option, index) => (
                      <Chip {...getTagProps({ index })} label={`${option.name}`} fontSize={14} />
                    ))
                  }
                  loading={loading}
                  noOptionsText="-"
                  onChange={(_, value) => {
                    setFieldValue('groups', value);
                    searchQuery('');
                  }}
                  isOptionEqualToValue={(option: SearchResponse | null, val: SearchResponse | null) =>
                    val?.uuid !== undefined && val?.uuid === option?.uuid
                  }
                  fullWidth
                />
              </EditRow>
            )}
            <EditRow name="Employee ID" disabled={!canEditOrgAttributes}>
              <StyledTextField
                id="employeeId"
                name="employeeId"
                value={values.employeeId}
                helperText={touched.employeeId ? errors.employeeId : ''}
                error={(touched.employeeId || false) && errors.employeeId !== undefined}
                onChange={handleChange}
                disabled={isSubmitting || !canEditOrgAttributes || isMapped(IDP_FIELDS.employeeId)}
                fullWidth
              />
            </EditRow>
            <EditRow name="Memorable word">
              <StyledTextField
                fullWidth
                id="personalDetails.verificationWord"
                name="personalDetails.verificationWord"
                value={values.personalDetails.verificationWord}
                disabled={isSubmitting}
                onChange={handleChange}
              />
            </EditRow>

            <ButtonWrapper>
              <Button variant="outlined" onClick={() => setEditing(false)} size="large">
                Cancel
              </Button>

              <Button variant="contained" type="submit" size="large" disabled={isSubmitting}>
                Save
              </Button>
            </ButtonWrapper>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default observer(PersonalInformation);
